import { CommonModule } from '@angular/common';
import { Component, DestroyRef, computed, effect, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { LanguageService, UILanguage } from './language.service';
import { ProductCatalogService, PublicCategory, PublicProduct } from './product-catalog.service';

type CatalogDictionary = {
  title: { en: string; nl: string; fr: string };
  subtitle: { en: string; nl: string; fr: string };
  filterLabel: { en: string; nl: string; fr: string };
  showFilters: { en: string; nl: string; fr: string };
  hideFilters: { en: string; nl: string; fr: string };
  clearFilters: { en: string; nl: string; fr: string };
  showMore: { en: string; nl: string; fr: string };
  details: { en: string; nl: string; fr: string };
  loading: { en: string; nl: string; fr: string };
  empty: { en: string; nl: string; fr: string };
};

const DICTIONARY: CatalogDictionary = {
  title: {
    en: 'Products Catalog',
    nl: 'Productcatalogus',
    fr: 'Catalogue Produits',
  },
  subtitle: {
    en: 'Explore products from BG Imports.',
    nl: 'Ontdek producten van BG Imports.',
    fr: 'Decouvrez les produits de BG Imports.',
  },
  filterLabel: {
    en: 'Shop by Categories',
    nl: 'Winkel op Categorieen',
    fr: 'Acheter par Categories',
  },
  showFilters: {
    en: 'Show Categories',
    nl: 'Toon Categorieen',
    fr: 'Afficher les Categories',
  },
  hideFilters: {
    en: 'Hide Categories',
    nl: 'Verberg Categorieen',
    fr: 'Masquer les Categories',
  },
  clearFilters: {
    en: 'Clear Filters',
    nl: 'Filters Wissen',
    fr: 'Effacer les Filtres',
  },
  showMore: {
    en: 'Show More',
    nl: 'Meer Tonen',
    fr: 'Afficher Plus',
  },
  details: {
    en: 'View Details',
    nl: 'Bekijk Details',
    fr: 'Voir Details',
  },
  loading: {
    en: 'Loading products...',
    nl: 'Producten laden...',
    fr: 'Chargement des produits...',
  },
  empty: {
    en: 'No products available yet.',
    nl: 'Nog geen producten beschikbaar.',
    fr: 'Aucun produit disponible pour le moment.',
  },
};

@Component({
  selector: 'app-product-catalog',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './product-catalog.component.html',
  styleUrl: './product-catalog.component.css',
})
export class ProductCatalogComponent {
  private readonly catalogService = inject(ProductCatalogService);
  private readonly languageService = inject(LanguageService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly pageSize = 8;
  private filterLoadingTimer: ReturnType<typeof setTimeout> | null = null;

  public readonly visibleCount = signal<number>(this.pageSize);
  public readonly selectedCategoryIDs = signal<string[]>([]);
  public readonly categoryLoading = signal<boolean>(false);
  public readonly categoryFilterOpen = signal<boolean>(false);
  public readonly language = this.languageService.language;
  public readonly products = this.catalogService.products;
  public readonly loading = this.catalogService.loading;
  public readonly error = this.catalogService.error;

  public readonly categoryOptions = computed<PublicCategory[]>(() => {
    const map = new Map<string, PublicCategory>();
    for (const product of this.products()) {
      for (const category of product.categories || []) {
        if (!map.has(category.id)) {
          map.set(category.id, category);
        }
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      this.categoryName(a).localeCompare(this.categoryName(b), undefined, { sensitivity: 'base' }),
    );
  });

  public readonly filteredProducts = computed<PublicProduct[]>(() => {
    const selectedIDs = this.selectedCategoryIDs();
    if (!selectedIDs.length) {
      return this.products();
    }

    const selected = new Set(selectedIDs);

    return this.products().filter((product) =>
      (product.categories || []).some((category) => selected.has(category.id)),
    );
  });

  public readonly visibleProducts = computed<PublicProduct[]>(() =>
    this.filteredProducts().slice(0, this.visibleCount()),
  );

  public readonly canShowMore = computed<boolean>(() =>
    this.visibleCount() < this.filteredProducts().length,
  );

  constructor() {
    void this.catalogService.load();

    this.destroyRef.onDestroy(() => {
      if (this.filterLoadingTimer) {
        clearTimeout(this.filterLoadingTimer);
      }
    });

    effect(() => {
      const total = this.products().length;
      if (total > 0 && this.visibleCount() > total) {
        this.visibleCount.set(total);
      }
    });
  }

  public t(key: keyof CatalogDictionary): string {
    return DICTIONARY[key][this.language()];
  }

  public showMore(): void {
    this.visibleCount.update((count) => count + this.pageSize);
  }

  public toggleCategoryFilter(rawCategoryID: string): void {
    const categoryID = String(rawCategoryID || '').trim();
    if (!categoryID) {
      return;
    }

    if (this.filterLoadingTimer) {
      clearTimeout(this.filterLoadingTimer);
    }

    this.categoryLoading.set(true);
    this.selectedCategoryIDs.update((current) => {
      if (current.includes(categoryID)) {
        return current.filter((id) => id !== categoryID);
      }
      return [...current, categoryID];
    });
    this.visibleCount.set(this.pageSize);

    this.filterLoadingTimer = setTimeout(() => {
      this.categoryLoading.set(false);
      this.filterLoadingTimer = null;
    }, 450);
  }

  public clearCategoryFilters(): void {
    if (!this.selectedCategoryIDs().length) {
      return;
    }
    this.selectedCategoryIDs.set([]);
    this.visibleCount.set(this.pageSize);
  }

  public isCategorySelected(categoryID: string): boolean {
    return this.selectedCategoryIDs().includes(categoryID);
  }

  public toggleCategoryFilterPanel(): void {
    this.categoryFilterOpen.update((open) => !open);
  }

  public productName(product: PublicProduct): string {
    return this.pickLocalizedValue(product.name_english, product.name_dutch, product.name_french);
  }

  public productDescription(product: PublicProduct): string {
    return this.pickLocalizedValue(
      product.description_english,
      product.description_dutch,
      product.description_french,
    );
  }

  public categoryName(category: PublicCategory): string {
    return this.pickLocalizedValue(category.name_english, category.name_dutch, category.name_french);
  }

  public imageURL(product: PublicProduct): string {
    return (
      product.product_image_link_1 ||
      product.product_image_link_2 ||
      product.product_image_link_3 ||
      product.product_image_link_4 ||
      product.product_image_link_5 ||
      'logo.png'
    );
  }

  public trackByProductID(_index: number, product: PublicProduct): string {
    return product.id;
  }

  private pickLocalizedValue(english: string, dutch: string, french: string): string {
    const value = this.pickByLanguage(this.language(), english, dutch, french).trim();
    if (value !== '') {
      return value;
    }

    return english || dutch || french || '-';
  }

  private pickByLanguage(language: UILanguage, english: string, dutch: string, french: string): string {
    if (language === 'nl') {
      return dutch || english || french;
    }
    if (language === 'fr') {
      return french || english || dutch;
    }
    return english || dutch || french;
  }
}
